import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DashboardWidgetId,
  DashboardWidgetSelection,
  UpdateDashboardWidgetsRequest,
} from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';
import { DASHBOARD_SUMMARY_KEY } from '@/hooks/use-dashboard';

/**
 * The tenant's dashboard widget selection (RL-377, concept Q4).
 *
 * Two different things arrive in one payload and must not be conflated:
 * `available` is the tenant's module entitlement — a licence — and `widgets`
 * is the presentation choice made inside it. The server owns entitlement, so
 * the picker trusts `available` rather than recomputing it from permissions;
 * a permission is not a licence.
 *
 * Selection is per tenant today and per user later (concept §5 phase 2), so
 * nothing here treats the list as global — the query key is the endpoint, and
 * a future per-user override changes only what the server resolves.
 */
const DASHBOARD_WIDGETS_KEY = ['dashboard-widgets'] as const;

export function useDashboardWidgets(enabled = true) {
  return useQuery({
    queryKey: DASHBOARD_WIDGETS_KEY,
    enabled,
    queryFn: async (): Promise<DashboardWidgetSelection> => {
      const response = await apiClient.get<DashboardWidgetSelection>('/dashboard/widgets');
      return response.data;
    },
  });
}

/**
 * Replace the tenant's selection. Order is meaningful — the list is the render
 * order — so the payload is sent exactly as the picker has it arranged.
 *
 * An empty list is the reset: it clears the tenant's stored choice and puts the
 * dashboard back on the built-in default, which the next GET reports as
 * `isDefault: true`. A dashboard with no tiles at all is not a configuration
 * anybody wants, so the empty array is free to carry that meaning.
 *
 * The dashboard page reads the same selection to decide what to render, so a
 * successful write invalidates it too.
 */
export function useUpdateDashboardWidgets() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      widgets: readonly DashboardWidgetId[],
    ): Promise<DashboardWidgetSelection> => {
      const body: UpdateDashboardWidgetsRequest = { widgets: [...widgets] };
      const response = await apiClient.put<DashboardWidgetSelection>('/dashboard/widgets', body);
      return response.data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: DASHBOARD_WIDGETS_KEY });
      await client.invalidateQueries({ queryKey: DASHBOARD_SUMMARY_KEY });
    },
  });
}
